import { isDeepEqual } from 'remeda';
import { ArrayParam, StringParam } from 'use-query-params';

import { IProjectAndCastingsFiltersForm } from '@/shared/types/filters';

export const defaultSalaryFilter = 0;
const defaultProjectCategoryId = 'all';

export const defaultFilterValues: IProjectAndCastingsFiltersForm = {
  name: '',
  casting_location_ids: [],
  salary: defaultSalaryFilter,
  total_salary: defaultSalaryFilter,
  work_permissions: [],
  filming_have_scenes: [],
  filming_have_not_scenes: [],
  gender: null,
  casting_role_id: null,
  experience: null,
  salary_period: null,
  availabilities: [],
  response_status: 'all',
  filming_location_ids: [],
  company_ids: [],
  project_category_id: defaultProjectCategoryId,
  only_my_projects: false,
  director_ids: [],
  producer_ids: [],
  scriptwriter_ids: []
};

export const filtersQueryParamsConfig = {
  name: StringParam,
  gender: StringParam,
  casting_location_ids: ArrayParam,
  salary: StringParam,
  total_salary: StringParam,
  salary_period: StringParam,
  casting_role_id: StringParam,
  work_permissions: ArrayParam,
  filming_restrictions: ArrayParam,
  filming_have_scenes: ArrayParam,
  project_category_id: StringParam,
  // Строкой, как и остальные поля: processFilters кладёт в URL JSON-строку ("true"/"false"),
  // а BooleanParam кодирует любую непустую строку в "1" — снятый флажок оставался включённым
  only_my_projects: StringParam,
  filming_have_not_scenes: ArrayParam,
  availabilities: ArrayParam,
  experience: StringParam,
  filming_location_ids: ArrayParam,
  company_ids: ArrayParam,
  director_ids: ArrayParam,
  producer_ids: ArrayParam,
  scriptwriter_ids: ArrayParam,
  response_status: StringParam
} as const;

/**
 * Поля формы, которые живут ВНЕ шторки: поиск шапки, таб «мои проекты», селекты
 * тулбара и статус отклика. Кнопка «Очистить всё» их не сбрасывает, поэтому и в
 * счётчик бейджа они не входят — иначе на чистой выдаче кастингов бейдж
 * показывал бы «1» из-за `only_my_projects`, включённого самой страницей
 */
const NON_DRAWER_FILTER_KEYS: readonly string[] = [
  'name',
  'only_my_projects',
  'project_category_id',
  'filming_location_ids',
  'response_status'
];

/**
 * Отличается ли поле формы от своего дефолта. Ключа может не быть вовсе
 * (частичная форма в тестах и на экранах, где панель показывает не всё) —
 * отсутствие значения это не «фильтр задан»
 * @param values - значения формы
 * @param key - ключ фильтра
 */
const isFilterChanged = (values: Record<string, unknown>, key: string) =>
  key in values &&
  !isDeepEqual(
    values[key],
    defaultFilterValues[key as keyof IProjectAndCastingsFiltersForm]
  );

/**
 * Число активных фильтров кастингов — то, что покажет бейдж «Фильтры» и что
 * сбросит «Очистить всё». Считается по значениям, а не по `dirtyFields` формы:
 * применение сохранённого набора делает `reset` и метит грязными ВСЕ поля,
 * из-за чего бейдж показывал два десятка вместо одного заданного фильтра
 * @param values - значения формы фильтров
 */
export const countActiveCastingFilters = (
  values: Record<string, unknown>
): number =>
  Object.keys(defaultFilterValues).filter(
    key => !NON_DRAWER_FILTER_KEYS.includes(key) && isFilterChanged(values, key)
  ).length;

/**
 * Поля панели фильтров проектов (таб «Проекты» актёра). Панель переиспользует ту
 * же форму и ту же обёртку, что кастинги, но показывает всего пять групп —
 * кастинговый счётчик на этом табе считал чужие ключи и врал
 */
const PROJECT_DRAWER_FILTER_KEYS: readonly string[] = [
  'filming_location_ids',
  'director_ids',
  'producer_ids',
  'scriptwriter_ids',
  'company_ids'
];

/**
 * Число активных фильтров проектов — бейдж шторки и счётчик шапки таба «Проекты»
 * @param values - значения формы фильтров
 */
export const countActiveProjectFilters = (
  values: Record<string, unknown>
): number =>
  PROJECT_DRAWER_FILTER_KEYS.filter(key => isFilterChanged(values, key)).length;
